import { nanoid } from 'nanoid';
import { AgentMode, AgentStreamError, Conversation, toAgentStreamError } from '~api';
import { createMessageEvent, createUserMessage, TApiClient } from '~portal/api';
import { logger } from '~portal/lib';
import { ConversationStore } from '~portal/stores';
import { AgentManager } from './AgentManager';
import { SocketManager } from './SocketManager';

/**
 * @desc Drives one conversation's turns (H2-172): `prompt` is a fire-and-forget tRPC mutation —
 * the run executes server-side decoupled from this client — and every event reaches the store
 * through the conversation event socket (see SocketBridge), not through this class. The agent's
 * remaining jobs are the optimistic user message, the created-conversation handshake, and
 * holding the socket open while a turn is expected.
 */
export class ChatAgent {
  readonly id: string;
  readonly createdAt = new Date();

  constructor(
    private conversationId: string,
    private readonly manager: AgentManager,
  ) {
    this.id = conversationId === 'new' ? nanoid() : conversationId;
  }

  /**
   * @desc Returns the ID used for Zustand store lookups.
   * For "new" conversations, returns the ephemeral agent ID until the accepted prompt provides
   * the real conversation ID.
   */
  get storeId() {
    if (this.conversationId !== 'new') return this.conversationId;
    return this.id;
  }

  /**
   * @desc Send a message: optimistic user event into the store, then the prompt mutation. The
   * accepted response carries the conversation (and its real id when newly created); events
   * stream in over the socket from the decoupled run.
   */
  sendMessage(
    trpc: TApiClient,
    message: string,
    mode: AgentMode.T | undefined,
    nav: (conversationId: string) => Promise<void>,
  ) {
    // The active flag is keyed by `storeId`, never `id`: once the conversation exists the socket
    // pipeline ends a turn under the real conversation id (socket-handlers `finishTurn`), so a
    // flag set under the ephemeral agent id would never be cleared and would block every later
    // prompt on this agent. `storeId` is the ephemeral id only until `handleCreated` rekeys it.
    if (ConversationStore.isAgentActive(this.storeId)) {
      logger.warn('[ChatAgent] Turn already active, ignoring prompt', {
        conversationId: this.conversationId,
        agentId: this.id,
      });
      return;
    }

    // Add optimistic user message to store (replaced by the server event, same id, on arrival).
    const userMessage = createUserMessage(message);
    const messageEvent = createMessageEvent('user', userMessage);
    ConversationStore.addHistoryEvent(this.storeId, messageEvent);
    ConversationStore.addActiveAgent(this.storeId);

    // Open the socket BEFORE the turn is accepted: the room is per-user, so events start
    // arriving the moment the run begins, whatever the conversation id turns out to be.
    SocketManager.acquire(this.storeId);

    trpc.client.conversations.prompt
      .mutate({
        message,
        conversationId: this.conversationId === 'new' ? undefined : this.conversationId,
        uid: messageEvent.id,
        mode,
      })
      .then(({ conversation, created }) => {
        if (created) this.handleCreated(trpc, messageEvent, conversation, nav);
      })
      .catch((err: unknown) => {
        // The turn was refused (fair-use limit, CONFLICT, shutdown, validation) or never
        // accepted. Classified here rather than server-side: with `prompt` a mutation, the
        // refusal IS the rejection, so tRPC hands the client an error with its code intact —
        // the flattening that forced classification into a yielded stream event is gone with
        // the subscription.
        logger.error('[ChatAgent] prompt rejected', {
          conversationId: this.conversationId,
          error: err instanceof Error ? err.message : String(err),
        });
        SocketManager.release(this.storeId);
        this.handleTurnError(toAgentStreamError(err));
      });
  }

  /**
   * @desc Attach to a conversation that may have a run in flight (page load/reload): ask the
   * server, and hold the socket open while the run lives. Resolves whether a run is active.
   */
  async resume(): Promise<boolean> {
    if (this.conversationId === 'new') return false;
    const active = await SocketManager.watch(this.conversationId);
    if (active) ConversationStore.addActiveAgent(this.conversationId);
    return active;
  }

  /**
   * @desc Show a classified turn failure and stop the agent.
   *
   * Without this the spinner merely stops and the turn is indistinguishable from a hang — which is
   * exactly how an exhausted fair-use allowance presented before any of this existed.
   */
  private handleTurnError(error: AgentStreamError) {
    ConversationStore.setTurnError(this.storeId, error);
    [this.id, this.conversationId].forEach(ConversationStore.removeActiveAgent);
  }

  private handleCreated(
    trpc: TApiClient,
    msg: Conversation.ClaudeMessageEvent,
    sync: Conversation.Serial,
    nav: (conversationId: string) => Promise<void>,
  ) {
    const ephemeralId = this.storeId;
    ConversationStore.addActiveAgent(sync._id);
    // The turn is now tracked under the real id; the ephemeral flag would otherwise linger
    // forever (nothing else ever removes it) and block every later prompt on this agent.
    if (ephemeralId !== sync._id) ConversationStore.removeActiveAgent(ephemeralId);
    this.conversationId = sync._id;
    if (sync._id !== this.id) this.manager.setAgentLookup(this.id, sync._id);
    SocketManager.rekey(ephemeralId, sync._id);
    ConversationStore.updateConversationFromSync(sync);
    // The optimistic user message, re-pushed under the real id. Still needed even though the
    // backend now persists the prompt when it accepts the turn (H2-172): `sync` is the
    // conversation as it was *before* that append, and `updateConversationFromSync` seeds the
    // raw history without building the grouped view — this call is what renders the turn. The
    // durable copy (same id, server turnId) replaces it when the run publishes it, and `get`
    // returns it on any later load.
    ConversationStore.addHistoryEvent(sync._id, msg);
    trpc.conversations.list.invalidate().catch((err) => {
      logger.error('[ChatAgent] Failed to invalidate conversations list', {
        conversationId: sync._id,
        error: err instanceof Error ? err.message : String(err),
      });
    });
    nav(sync._id).catch((err) => {
      logger.error('[ChatAgent] Navigation failed after prompt accepted', {
        conversationId: sync._id,
        error: err instanceof Error ? err.message : String(err),
      });
    });
  }

  /**
   * @desc Clean up this agent's view-side state. Deliberately does NOT release the socket: a
   * turn in flight keeps streaming into the store until TurnComplete or a terminal error
   * releases it (SocketBridge) — navigating away never interrupts the run.
   */
  teardown() {
    ConversationStore.removeActiveAgent(this.id);
  }
}
